import {
  InputText,
  Anchor,
  Button,
  DataButton,
  Icon,
  InputContainer,
  ListItem,
  TabbedDrawer,
  H4,
  TabbedDrawerHeader,
  TabbedDrawerTitle,
  TabbedDrawerTab,
  TabbedDrawerCTA,
} from "@sellar-io/components";
import { Alert } from "../../../components/alert/Alert";
import { usePriceTierList } from "../../../hooks/api/v2/priceTier/usePriceTierList";
import { pluralize } from "@sellar-io/common";
import { useNavigate } from "react-router";
import * as ROUTES from "../../../constants/routes";
import { useCustomerZonesUpdate } from "../../../hooks/api/v2/customerZones/useCustomerZonesUpdate";
import { useState } from "react";
import { useDebounce } from "use-debounce";
import { InfiniteList } from "../../../components/infiniteLoading/InfiniteList";
import { usePriceTier } from "../../../hooks/api/v2/priceTier/usePriceTier";
import { PriceListContent } from "../../priceLists/PriceList";
import { useRemoteFeatureFlags } from "../../../hooks/useRemoteFeatureFlags";
import { PriceListContext } from "../../priceLists/PriceListContext";
import { getPriceListDiscountSummary } from "../../priceLists/utils/priceListDiscountSummary";

import { twMerge } from "tailwind-merge";

export const CustomerZonePriceTier = ({
  customerZoneName,
  customerZoneId,
  currentPriceTierId,
}: {
  customerZoneName?: string | null;
  customerZoneId: number;
  currentPriceTierId: number | null;
}) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [search, setSearch] = useState("");
  const [sideDrawerActive, setSideDrawerActive] = useState(false);
  const [selectedPriceListId, setSelectedPriceListId] = useState<number>();

  const [debouncedSearch] = useDebounce(search, 500);

  const priceTiersQuery = usePriceTierList({
    includeCounts: true,
    search: debouncedSearch,
    limit: 10,
    query: [
      {
        type: "derivationTypeIncludes",
        value: ["custom", "flat", "percentage"],
        modifier: "IN",
      },
    ],
  });

  const priceTiers = priceTiersQuery.data?.pages.flatMap((p) => p) ?? [];

  const customerZoneUpdate = useCustomerZonesUpdate(customerZoneId);

  const updateCustomerZone = (PriceTierId: number) => {
    const priceTier = priceTiers.find((at) => at.id === PriceTierId);

    customerZoneUpdate.mutate(
      {
        PriceTierId,
      },
      {
        onError: () => {
          setError(
            `Failed to update customer zone with Price Tier: ${priceTier?.name}`,
          );
        },
      },
    );
  };

  const unbundlingRelease2BEnabled = useRemoteFeatureFlags().isActive(
    "unbundlingRelease2B",
  );

  return (
    <>
      <div className="flex flex-col gap-4 px-4">
        {error && (
          <Alert type="error" title="Failed to update" message={error} />
        )}
        <div className="flex flex-col gap-1 border-b border-zinc-300 pb-4">
          {unbundlingRelease2BEnabled ? (
            <>
              <H4 className="font-semibold">Price lists</H4>
              <p className="text-zinc-500">
                Control what pricing customers that fall into your Local
                Customer Zone will see. For more information or help setting up
                your price books, visit our{" "}
                <Anchor
                  className="inline-flex items-center gap-1 text-blue-600 underline"
                  href="https://intercom.help/sellario/en/articles/12008469-supplier-guide-product-catalogues"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  help centre
                  <Icon icon="ARROW_TOP_RIGHT" size={0.6} />
                </Anchor>
                .
              </p>
            </>
          ) : (
            <>
              <H4 className="font-semibold">Price tier</H4>
              <p className="text-zinc-500">
                Control what pricing customers that fall into your{" "}
                {customerZoneName} Customer Zone will see.
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <InputContainer className="w-full py-[0.4rem] md:max-w-[250px] md:flex-none">
            <InputText
              required={false}
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full"
            />
          </InputContainer>
          <div className="flex items-center gap-2">
            <DataButton
              className="flex items-center gap-1"
              border={true}
              onClick={() => navigate(ROUTES.PRICE_TIERS)}
            >
              <Icon icon="PLUS" />
              New
            </DataButton>
          </div>
        </div>

        <InfiniteList
          query={priceTiersQuery}
          className="mb-8 flex flex-col gap-2"
        >
          {priceTiers.map((priceTier) => {
            const selected = priceTier.id === currentPriceTierId;

            return (
              <ListItem
                key={priceTier.id}
                title={priceTier.name}
                subtitle={getPriceListDiscountSummary({ counts: priceTier })}
                icon={selected ? "CHECK" : "TAG_MULTIPLE_OUTLINE"}
                actionText={unbundlingRelease2BEnabled ? "Preview" : "View"}
                onClick={() => {
                  if (unbundlingRelease2BEnabled) {
                    setSelectedPriceListId(priceTier.id);
                    setSideDrawerActive(true);
                  } else {
                    navigate(
                      ROUTES.PRICE_TIER.href({ priceTierId: priceTier.id }),
                    );
                  }
                }}
                iconClick={() => updateCustomerZone(priceTier.id)}
                className={twMerge(
                  "rounded-lg bg-white p-4 hover:bg-zinc-50",
                  selected ? "border border-zinc-300 bg-zinc-50" : "",
                )}
                iconClassName={selected ? "border-zinc-800 bg-zinc-200" : ""}
              />
            );
          })}
        </InfiniteList>
      </div>
      <PriceListPreview
        customerZoneId={customerZoneId}
        active={sideDrawerActive}
        setActive={(value: boolean) => {
          setSideDrawerActive(value);
          if (!sideDrawerActive) setSelectedPriceListId(undefined);
        }}
        priceListId={selectedPriceListId}
        assign={() => {
          if (selectedPriceListId) updateCustomerZone(selectedPriceListId);
          setSideDrawerActive(false);
          if (!sideDrawerActive) setSelectedPriceListId(undefined);
        }}
      />
    </>
  );
};

const PriceListPreview = ({
  customerZoneId,
  active,
  setActive,
  priceListId,
  assign,
}: {
  customerZoneId: number;
  active: boolean;
  setActive: (value: boolean) => void;
  priceListId?: number;
  assign: () => void;
}) => {
  return (
    <TabbedDrawer active={active} setActive={setActive} initTab={0}>
      {active && priceListId && (
        <Drawer
          customerZoneId={customerZoneId}
          priceListId={priceListId}
          assign={assign}
        />
      )}
    </TabbedDrawer>
  );
};

const Drawer = ({
  customerZoneId,
  priceListId,
  assign,
}: {
  customerZoneId: number;
  priceListId: number;
  assign: () => void;
}) => {
  const navigate = useNavigate();
  const { data: priceList, isLoading } = usePriceTier(priceListId);

  if (!priceList || isLoading) return <></>;

  const numberOfCustomers = priceList.SupplierRetailerConnections.length;

  return (
    <>
      <TabbedDrawerHeader>
        <TabbedDrawerTitle
          title={priceList.name}
          subtitle={`Assigned to ${numberOfCustomers} ${pluralize("customer", numberOfCustomers)}`}
        />
      </TabbedDrawerHeader>
      <TabbedDrawerTab tab={0}>
        <PriceListContext value={{}}>
          <PriceListContent priceList={priceList} readOnly={true} />
        </PriceListContext>
      </TabbedDrawerTab>
      <TabbedDrawerCTA className="flex-row">
        <Button
          className="flex w-full items-center gap-1"
          color="transparent"
          onClick={() =>
            navigate(ROUTES.PRICE_LIST.href({ priceListId }), {
              state: {
                customerZoneRedirectId: customerZoneId,
              },
            })
          }
        >
          <Icon icon="PENCIL" />
          Edit
        </Button>
        <Button className="w-full" onClick={assign}>
          Assign
        </Button>
      </TabbedDrawerCTA>
    </>
  );
};
